import type { Rectangle } from 'pixi.js';
import type { DynamicInferredDimensions } from '../../../../enums';
import { DynamicLevel, DynamicXAlign, DynamicYAlign } from '../../../../enums';
import type { CanvasTextMetrics } from '../metrics/CanvasTextMetrics';
import type { DynamicGraphicType } from '../types';
import type { AnimationConfig } from '../../../../libs/AnimationCurveManager';
import { CanvasTextDynamicGraphicAnimationRenderer } from './CanvasTextDynamicGraphicAnimationRenderer';
import type { CanvasTextDynamicAnimationRendererSettings } from './CanvasTextDynamicAnimationBase';
import type { CanvasTextDynamicCircleRendererSettings } from './CanvasTextDynamicCircleRenderer';
import type { CanvasTextDynamicBoxRendererSettings } from './CanvasTextDynamicBoxRenderer';
import type { CanvasTextDynamicBoxBorderRendererSettings } from './CanvasTextDynamicBoxBorderRenderer';

export interface CanvasTextDynamicGraphicRendererSettingsBase {
    type: DynamicGraphicType;
    animationConfigurations: CanvasTextDynamicAnimationRendererSettings[];
    enabled: boolean;
    level: DynamicLevel;
    alignX: DynamicXAlign;
    alignY: DynamicYAlign;
    positionX: number;
    positionY: number;
    asMask: boolean;
}

export type CanvasTextDynamicGraphicRendererSettings =
    | CanvasTextDynamicCircleRendererSettings
    | CanvasTextDynamicBoxRendererSettings
    | CanvasTextDynamicBoxBorderRendererSettings;

export type Position = {
    x: number;
    y: number;
};

export const WORD_LEVEL_ALIGN_Y_OPTIONS = [
    DynamicYAlign.maxTop,
    DynamicYAlign.averageCenter,
    DynamicYAlign.baseline,
    DynamicYAlign.maxBottom,
];

/**
 * The list of animatable properties that is supported by all graphics
 */
export enum AnimatableGeometricGraphicProperties {
    x = 'x',
    y = 'y',
}
export enum AnimatableNonGeometricGraphicProperties {
    alpha = 'alpha',
}

/**
 * CanvasTextDynamicGraphic is the base class for rendering graphics behind a block of text.
 */
export abstract class CanvasTextDynamicGraphicRenderer {
    enabled = false;
    level = DynamicLevel.object;
    alignX = DynamicXAlign.left;
    alignY = DynamicYAlign.top;
    positionX = 0;
    positionY = 0;
    asMask = false;
    abstract readonly type: DynamicGraphicType;

    abstract animationConfigurations: CanvasTextDynamicGraphicAnimationRenderer[];
    #bounds: Rectangle = null;

    addAnimationConfiguration(animationConfiguration: CanvasTextDynamicGraphicAnimationRenderer) {
        this.animationConfigurations.push(animationConfiguration);
    }

    apply(settings: CanvasTextDynamicGraphicRendererSettings) {
        if (settings.type !== this.type) {
            throw new Error(`Using the incorrect graphic class, ${settings.type} !== ${this.type}`);
        }
        this.enabled = settings.enabled;
        this.level = settings.level;
        this.alignX = settings.alignX;
        this.alignY = settings.alignY;
        this.positionX = settings.positionX;
        this.positionY = settings.positionY;

        this.animationConfigurations = [];
        settings.animationConfigurations.forEach((animationConfig) => {
            const newAnimationConfiguration = new CanvasTextDynamicGraphicAnimationRenderer();
            newAnimationConfiguration.apply(animationConfig);
            this.addAnimationConfiguration(newAnimationConfiguration);
        });
        this.asMask = settings.asMask;
    }

    build(_metrics: CanvasTextMetrics) {
        this._clearCachedValues();
    }

    buildAnimationManagerConfig(containerDuration: number): AnimationConfig[] {
        const animationConfigs: AnimationConfig[] = [];
        this.animationConfigurations.forEach((config) => {
            const animationConfig = config.buildAnimationManagerConfig(
                this._getInferredValues.bind(this),
                this._getOffsetForProperty.bind(this),
                containerDuration,
            );
            if (animationConfig) {
                animationConfigs.push(animationConfig);
            }
        });
        return animationConfigs;
    }

    get bounds(): Rectangle {
        if (this.#bounds === null) {
            this.#bounds = this._getBounds();
        }
        return this.#bounds;
    }

    /** The list of properties that can be animated during draw */
    protected static get baseAnimatableProperties() {
        return [
            ...Object.values(AnimatableGeometricGraphicProperties),
            ...Object.values(AnimatableNonGeometricGraphicProperties),
        ];
    }

    /**
     * The list of geometric properties that can be animated during draw. These properties affect the position and shape of
     * the element being drawn. The properties in this list will be remapped from the reference resolution to the output
     * and should exclude any normalized properties such as scale.
     **/
    protected static get baseAnimatableGeometricProperties() {
        return Object.values(AnimatableGeometricGraphicProperties);
    }

    abstract draw(context: CanvasRenderingContext2D): void;

    abstract clipElement(context: CanvasRenderingContext2D, elementIndex: number): void;

    abstract _getBounds(): Rectangle;

    /**
     * Returns the inferred values for the given dimension.
     * Note that not all inferred dimensions are supported. You may notice that the graphic implementations don't
     * accept the level. This is because graphics are first built based on the text metrics and their level.
     * The animations are then built after and the configs are based on the already built graphics. For example:
     *  - It doesn't make sense to create a line level box, but then animate one of its properties at the word level.
     *  - Inferred dimensions like gapless don't make sense for graphic animation because gapless
     *    should be used to create the graphic. If we want to animate a property based on the "gapless" height,
     *    it's just the current height of the graphic it was already built with the gapless height.
     */
    protected abstract _getInferredValues(inferredDimension: DynamicInferredDimensions): number[];

    /**
     * Applies an offset to the driven range to create the final driven range based on shouldOffsetPropertyValue.
     */
    protected abstract _getOffsetForProperty(
        property: string,
        shouldOffsetPropertyValue: boolean,
    ): number;

    /**
     * The number of shapes that this graphic will draw. This depends on the level of the graphic.
     * For example, a graphic that is drawn at the word level will draw a shape for each word.
     */
    abstract get _numberOfShapes(): number;

    protected _clearCachedValues() {
        this.#bounds = null;
    }
}
